import {Injectable} from '@angular/core';
import {HAND_CONNECTIONS, Hands} from '@mediapipe/hands';
import {Pose, POSE_CONNECTIONS} from '@mediapipe/pose';
import {
  FaceMesh,
  FACEMESH_FACE_OVAL,
  FACEMESH_LEFT_EYE,
  FACEMESH_LEFT_EYEBROW,
  FACEMESH_LEFT_IRIS,
  FACEMESH_LIPS,
  FACEMESH_RIGHT_EYE,
  FACEMESH_RIGHT_EYEBROW,
  FACEMESH_RIGHT_IRIS,
  FACEMESH_TESSELATION
} from '@mediapipe/face_mesh';
import {drawConnectors, drawLandmarks} from '@mediapipe/drawing_utils';
import {interval} from 'rxjs';
import {UploadService} from '../upload.service';

const CONSTRAINS = {
  video: {
    width: {
      ideal: 640
    },
    height: {
      ideal: 480
    },
    frameRate: {
      ideal: 10
    }
  },
  audio: false
};

@Injectable({
  providedIn: 'root'
})
export class CameraService {
  public videoElement: any;
  public canvasElement: any;
  // public landmarkContainer: any;
  public localStream: MediaStream | undefined | null;
  public errMsg: any;
  public cameraIsEnable: boolean;
  ctx: any;
  // grid: any;
  interval: any;
  hands?: Hands;
  pose?: Pose;
  faceMesh?: FaceMesh;

  prepared = false;

  model = this.uploadService.MODEL_HANDS;
  loading = false;

  constructor(private uploadService: UploadService) {
    this.cameraIsEnable = false;
  }

  public initVideoElement(videoElement: HTMLVideoElement, canvasElement: HTMLCanvasElement/*, landmarkContainer: any*/): void {
    this.videoElement = videoElement;
    this.canvasElement = canvasElement;
    this.ctx = this.canvasElement.getContext('2d');
    /*this.landmarkContainer = landmarkContainer;
    this.grid = new LandmarkGrid(landmarkContainer);*/
  }

  public async prepareHands(): Promise<void> {
    this.hands = new Hands({
      locateFile: (path) => {
        console.log('file');
        console.log(path);
        return `assets/mediapipeHands/${path}`;
      }
    });
    if (this.hands) {
      this.hands.setOptions({
        maxNumHands: 2,
        modelComplexity: 1,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5
      });

      this.hands.onResults(this.onHandResults);
      return this.hands?.initialize().then(() => {
        console.log('Initialized Hands!!!');
      }).catch(err => console.log(err));
    }
  }

  public async preparePose(): Promise<void> {
    this.pose = new Pose({
      locateFile: (path) => {
        console.log('file');
        console.log(path);
        return `assets/mediapipePose/${path}`;
      }
    });
    if (this.pose) {
      this.pose.setOptions({
        modelComplexity: 1,
        smoothLandmarks: true,
        enableSegmentation: true,
        smoothSegmentation: true,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5
      });

      this.pose.onResults(this.onPoseResults);
      return this.pose?.initialize().then(() => {
        console.log('Initialized Pose!!!');
      }).catch(err => console.log(err));
    }
  }

  public async prepareAllModels(): Promise<void> {
    if (!this.prepared) {
      this.loading = true;
      await this.preparePose();
      await this.prepareHands();
      await this.prepareFace();
      this.loading = false;
      this.prepared = true;
    }
  }

  public async prepareFace(): Promise<void> {
    this.faceMesh = new FaceMesh({
      locateFile: (path) => {
        console.log('file');
        console.log(path);
        return `assets/mediapipeFace/${path}`;
      }
    });
    if (this.faceMesh) {
      this.faceMesh.setOptions({
        maxNumFaces: 1,
        refineLandmarks: true,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5
      });

      this.faceMesh.onResults(this.onFaceResults);
      return this.faceMesh?.initialize().then(() => {
        console.log('Initialized Face!!!');
      }).catch(err => console.log(err));
    }
  }


  public copyToCanvas(): void {
    this.interval?.unsubscribe();
    this.interval = interval(1000 / 10 /* fps */)
      .subscribe(async () => {
        if (this.hands && this.model === this.uploadService.MODEL_HANDS) {
          await this.hands.send({image: this.videoElement});
        } else if (this.faceMesh && this.model === this.uploadService.MODEL_FACE) {
          await this.faceMesh.send({image: this.videoElement});
        } else if (this.pose && this.model === this.uploadService.MODEL_POSE) {
          await this.pose.send({image: this.videoElement});
        }
      });
  }

  onHandResults = (results: any): void => {
    this.onResultCallback(() => {
      if (results.multiHandLandmarks) {
        for (const landmarks of results.multiHandLandmarks) {
          drawConnectors(this.ctx, landmarks, HAND_CONNECTIONS,
            {color: '#00FF00', lineWidth: 5});
          drawLandmarks(this.ctx, landmarks, {color: '#FF0000', lineWidth: 2});

          /*const vector: number[] = [];
          landmarks.forEach((landmark: NormalizedLandmark) => {
            vector.push(landmark.x);
            vector.push(landmark.y);
            vector.push(landmark.z);
          });*/
        }
      }
    }, results.image);
  }

  onPoseResults = (results: any): void => {
    if (!results.poseLandmarks) {
      // this.grid.updateLandmarks([]);
      return;
    }
    this.ctx.save();
    this.ctx.clearRect(0, 0, this.canvasElement.width, this.canvasElement.height);
    /*this.ctx.drawImage(results.segmentationMask, 0, 0,
      this.canvasElement.width, this.canvasElement.height);*/

    // Only overwrite existing pixels.
    this.ctx.globalCompositeOperation = 'source-in';
    this.ctx.fillStyle = '#00FF00';
    this.ctx.fillRect(0, 0, this.canvasElement.width, this.canvasElement.height);

    // Only overwrite missing pixels.
    this.ctx.globalCompositeOperation = 'destination-atop';
    this.ctx.drawImage(
      results.image, 0, 0, this.canvasElement.width, this.canvasElement.height);

    this.ctx.globalCompositeOperation = 'source-over';
    drawConnectors(this.ctx, results.poseLandmarks, POSE_CONNECTIONS,
      {color: '#00FF00', lineWidth: 4});
    drawLandmarks(this.ctx, results.poseLandmarks,
      {color: '#FF0000', lineWidth: 2});
    this.ctx.restore();

    // this.grid.updateLandmarks(results.poseWorldLandmarks);
  }

  onFaceResults = (results: any): void => {
    this.onResultCallback(() => {
      if (results.multiFaceLandmarks) {
        for (const landmarks of results.multiFaceLandmarks) {
          drawConnectors(this.ctx, landmarks, FACEMESH_TESSELATION,
            {color: '#C0C0C070', lineWidth: 1});
          drawConnectors(this.ctx, landmarks, FACEMESH_RIGHT_EYE, {color: '#FF3030'});
          drawConnectors(this.ctx, landmarks, FACEMESH_RIGHT_EYEBROW, {color: '#FF3030'});
          drawConnectors(this.ctx, landmarks, FACEMESH_RIGHT_IRIS, {color: '#FF3030'});
          drawConnectors(this.ctx, landmarks, FACEMESH_LEFT_EYE, {color: '#30FF30'});
          drawConnectors(this.ctx, landmarks, FACEMESH_LEFT_EYEBROW, {color: '#30FF30'});
          drawConnectors(this.ctx, landmarks, FACEMESH_LEFT_IRIS, {color: '#30FF30'});
          drawConnectors(this.ctx, landmarks, FACEMESH_FACE_OVAL, {color: '#E0E0E0'});
          drawConnectors(this.ctx, landmarks, FACEMESH_LIPS, {color: '#E0E0E0'});
        }
      }
    }, results.image);
  }

  onResultCallback = (callback: any, image: any): void => {
    this.ctx.save();
    this.ctx.clearRect(0, 0, this.canvasElement.width, this.canvasElement.height);
    this.ctx.drawImage(
      image, 0, 0, this.canvasElement.width, this.canvasElement.height);
    if (callback) {
      callback();
    }
    this.ctx.restore();
  }

  public startCamera(callback: CallableFunction | null = null): void {
    console.log('start Video');
    this.errMsg = undefined;
    if (this.localStream) {
      this.stopCamera();
    }
    this.cameraIsEnable = false;
    navigator.mediaDevices.getUserMedia(CONSTRAINS)
      .then(stream => {
        this.localStream = stream;
        this.videoElement.srcObject = stream;
        this.videoElement.muted = true;
        this.cameraIsEnable = true;
        if (callback) {
          callback(stream);
        }
      })
      .catch(error => {
        if (error) {
          console.log(error.message);
        }
        this.errMsg = 'Camera not found';
        console.log('catch mediaDevices.getUserMedia(CONSTRAINS)');
      });
  }

  public stopCamera(): void {
    this.interval?.unsubscribe();
    this.interval = null;
    console.log('stopCamera');
    if (this.localStream) {
      const tracks = this.localStream.getTracks();
      tracks.forEach((track: MediaStreamTrack) => {
        track.stop();
      });
      this.localStream = null;
    }
  }
}
